'use client'

import { createGetStreamProviderNotificationClient } from '@app/care/v1/video/adapters/getstream/GetStreamProviderNotificationClient'
import type {
  ProviderNotification,
  ProviderNotificationSnapshot,
} from '@app/care/v1/video/contracts/ProviderNotificationClient'
import { useStableVideoServiceParams } from '@app/care/v1/video/hooks/useStableVideoServiceParams'
import { COOKIE_KEYS } from 'constants/common'
import useThriveAuth from 'hooks/useThriveAuth'
import { getProviderDataFromToken } from 'hooks/useThriveAuth/helpers'
import Cookies from 'js-cookie'
import { Bell } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { getProviderDataFromSuperAdminData } from 'utils/superAdmin/superAdminUtils'

const EMPTY: ProviderNotificationSnapshot = {
  notifications: [],
  unreadCount: 0,
}

/** Toast joins that happened just now — including the first SSE frame after connect. */
const RECENT_JOIN_TOAST_MS = 2 * 60 * 1000

function isRecentUnread(notification: ProviderNotification): boolean {
  if (notification.readAt) {
    return false
  }

  const createdAt = new Date(notification.createdAt).getTime()

  if (!Number.isFinite(createdAt)) {
    return false
  }

  const ageMs = Date.now() - createdAt
  return ageMs >= 0 && ageMs <= RECENT_JOIN_TOAST_MS
}

/** True only when the user is actively operating as a provider. */
function hasTokenProviderIdentity(
  accessToken: string | null | undefined
): boolean {
  try {
    return Boolean(accessToken && getProviderDataFromToken(accessToken))
  } catch {
    return false
  }
}

function hasActAsClinicianSession(): boolean {
  try {
    const actedAsProvider = getProviderDataFromSuperAdminData(
      Cookies.get(COOKIE_KEYS.SUPERADMIN_DATA) ?? ''
    )
    return Boolean(
      actedAsProvider?.providerId && actedAsProvider?.ehrProfileCode
    )
  } catch {
    return false
  }
}

function isSupportSurface(pathname: string): boolean {
  return pathname.startsWith('/app/support') || pathname.startsWith('/support')
}

export default function ProviderWaitingNotifications(): JSX.Element | null {
  const {
    isAuthenticated,
    isClinician,
    isClinicalAdmin,
    isSupportUser,
    getAccessTokenSilently,
  } = useThriveAuth()
  const [pathname, setPathname] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : ''
  )

  useEffect(() => {
    const syncPath = () => setPathname(window.location.pathname)
    syncPath()
    window.addEventListener('popstate', syncPath)
    return () => window.removeEventListener('popstate', syncPath)
  }, [])

  // Native clinicians with a provider profile, or clinical admins who used
  // "Act as Clinician". Support-only sessions (even with a mapped providerId
  // in the JWT) must not see the bell — and never on support surfaces.
  const enabled = Boolean(
    isAuthenticated &&
      !isSupportSurface(pathname) &&
      ((isClinicalAdmin && hasActAsClinicianSession()) ||
        (isClinician &&
          !isSupportUser &&
          hasTokenProviderIdentity(getAccessTokenSilently())))
  )
  const params = useStableVideoServiceParams()
  const client = useMemo(
    () => createGetStreamProviderNotificationClient(params),
    [params]
  )
  const [snapshot, setSnapshot] = useState(EMPTY)
  const [open, setOpen] = useState(false)
  const initializedRef = useRef(false)
  const knownIdsRef = useRef(new Set<string>())
  const broadcastRef = useRef<BroadcastChannel | null>(null)

  useEffect(() => {
    if (!enabled || typeof BroadcastChannel === 'undefined') {
      return
    }

    const channel = new BroadcastChannel('provider-waiting-notifications')
    broadcastRef.current = channel

    channel.onmessage = (event) => {
      if (typeof event.data === 'string') {
        knownIdsRef.current.add(event.data)
      }
    }

    return () => {
      channel.close()
      broadcastRef.current = null
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) {
      initializedRef.current = false
      knownIdsRef.current.clear()
      setSnapshot(EMPTY)
      return
    }

    return client.subscribe((next) => {
      const newItems = next.notifications
        .filter((item) => !knownIdsRef.current.has(item.id))
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )

      newItems.forEach((item) => knownIdsRef.current.add(item.id))

      // First SSE frame used to seed known IDs only — so a patient who joined
      // while the stream was connecting updated the bell with no popup.
      const toasts = initializedRef.current
        ? newItems
        : newItems.filter(isRecentUnread)

      if (!initializedRef.current) {
        initializedRef.current = true
      }

      toasts.forEach((item) => {
        broadcastRef.current?.postMessage(item.id)
        showWaitingToast(item, () => {
          void client.markRead(item.id)
        })
      })
      setSnapshot(next)
    })
  }, [client, enabled])

  if (!enabled) {
    return null
  }

  const markReadAndOpen = async (notification: ProviderNotification) => {
    setSnapshot((current) => ({
      notifications: current.notifications.map((item) =>
        item.id === notification.id
          ? { ...item, readAt: item.readAt ?? new Date().toISOString() }
          : item
      ),
      unreadCount: Math.max(
        0,
        current.unreadCount - (notification.readAt ? 0 : 1)
      ),
    }))
    await client.markRead(notification.id).catch(() => undefined)
    window.location.href = `/care/v1/video/call/${notification.sessionId}`
  }

  const markAllRead = async () => {
    const now = new Date().toISOString()
    setSnapshot((current) => ({
      notifications: current.notifications.map((item) => ({
        ...item,
        readAt: item.readAt ?? now,
      })),
      unreadCount: 0,
    }))
    await client.markAllRead().catch(() => undefined)
  }

  return (
    <div className="~fixed ~right-6 ~top-[10.5rem] ~z-[1000]">
      <button
        type="button"
        className="~relative ~flex ~h-11 ~w-11 ~items-center ~justify-center ~border-0 ~bg-transparent ~p-0 ~text-charcoal/70 hover:~text-charcoal"
        aria-label={`Waiting room notifications${
          snapshot.unreadCount ? `, ${snapshot.unreadCount} unread` : ''
        }`}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Bell aria-hidden className="~h-[22px] ~w-[22px]" strokeWidth={1.8} />
        {snapshot.unreadCount > 0 ? (
          <span className="~absolute ~-right-1 ~-top-1 ~flex ~h-5 ~min-w-5 ~items-center ~justify-center ~rounded-full ~bg-cherry ~px-1 ~text-[10px] ~font-semibold ~text-white">
            {snapshot.unreadCount > 99 ? '99+' : snapshot.unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <aside className="~absolute ~right-0 ~mt-2 ~w-[min(22rem,calc(100vw-2rem))] ~overflow-hidden ~rounded-md ~border ~border-charcoal/10 ~bg-white">
          <header className="~flex ~items-center ~justify-between ~border-b ~border-charcoal/10 ~px-4 ~py-3">
            <p className="~m-0 ~text-sm ~font-semibold ~text-charcoal">
              Waiting room
            </p>
            {snapshot.unreadCount > 0 ? (
              <button
                type="button"
                className="~text-xs ~text-navy ~underline"
                onClick={() => void markAllRead()}
              >
                Mark all read
              </button>
            ) : null}
          </header>

          <div className="~max-h-80 ~overflow-y-auto">
            {snapshot.notifications.length === 0 ? (
              <p className="~m-0 ~px-4 ~py-6 ~text-center ~text-sm ~text-charcoal/60">
                No waiting-room notifications
              </p>
            ) : (
              snapshot.notifications.slice(0, 20).map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className={`~block ~w-full ~border-0 ~border-b ~border-charcoal/10 ~px-4 ~py-3 ~text-left ${
                    notification.readAt ? '~bg-white' : '~bg-navy/5'
                  }`}
                  onClick={() => void markReadAndOpen(notification)}
                >
                  <span className="~block ~text-sm ~font-medium ~text-charcoal">
                    {notification.patientName} is waiting
                  </span>
                  <span className="~mt-1 ~block ~text-xs ~text-charcoal/60">
                    {formatTime(notification.createdAt)}
                  </span>
                </button>
              ))
            )}
          </div>
        </aside>
      ) : null}
    </div>
  )
}

function showWaitingToast(
  notification: ProviderNotification,
  markRead: () => void
) {
  playWaitingNotificationSound()
  toast.info(notification.title, {
    id: `waiting-${notification.id}`,
    description: `${notification.patientName} is waiting for their session.`,
    duration: 12_000,
    position: 'top-center',
    action: {
      label: 'Open session',
      onClick: () => {
        markRead()
        window.location.href = `/care/v1/video/call/${notification.sessionId}`
      },
    },
  })
}

let sharedAudioContext: AudioContext | null = null

/**
 * Soft meeting-style join chime (Teams/Zoom-like arpeggio, no asset file).
 * Browsers may block until the provider has interacted with the page once.
 */
function playWaitingNotificationSound() {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const AudioCtx =
      window.AudioContext ||
      ((window as unknown) as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext

    if (!AudioCtx) {
      return
    }

    if (!sharedAudioContext || sharedAudioContext.state === 'closed') {
      sharedAudioContext = new AudioCtx()
    }

    const ctx = sharedAudioContext
    void ctx.resume().then(() => {
      const now = ctx.currentTime
      // Warm major-triad arpeggio + soft sustained top note (meeting join feel).
      const notes = [
        { frequency: 523.25, start: 0, duration: 0.28, peak: 0.07 }, // C5
        { frequency: 659.25, start: 0.11, duration: 0.3, peak: 0.08 }, // E5
        { frequency: 783.99, start: 0.22, duration: 0.45, peak: 0.09 }, // G5
      ]

      for (const note of notes) {
        const startAt = now + note.start
        const endAt = startAt + note.duration

        // Fundamental
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = 'sine'
        osc.frequency.value = note.frequency
        gain.gain.setValueAtTime(0.0001, startAt)
        gain.gain.exponentialRampToValueAtTime(note.peak, startAt + 0.04)
        gain.gain.exponentialRampToValueAtTime(0.0001, endAt)
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(startAt)
        osc.stop(endAt + 0.02)

        // Soft upper harmonic for a bell / meeting-chime character
        const harmonic = ctx.createOscillator()
        const harmonicGain = ctx.createGain()
        harmonic.type = 'triangle'
        harmonic.frequency.value = note.frequency * 2
        harmonicGain.gain.setValueAtTime(0.0001, startAt)
        harmonicGain.gain.exponentialRampToValueAtTime(
          note.peak * 0.25,
          startAt + 0.03
        )
        harmonicGain.gain.exponentialRampToValueAtTime(0.0001, endAt)
        harmonic.connect(harmonicGain)
        harmonicGain.connect(ctx.destination)
        harmonic.start(startAt)
        harmonic.stop(endAt + 0.02)
      }
    })
  } catch {
    // Autoplay / unsupported — toast still shows.
  }
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}
